/**
 * Singleton refresh-token rotation.
 *
 * BOTH the boot-time refresh in [main.tsx] AND the auto-retry in
 * [api/client.ts] go through this single in-flight promise. Without this
 * coordination, two parallel `POST /v1/auth/refresh` calls would present
 * the SAME cookie, the second would land after the first had already
 * rotated it, and the server's family-revoke-on-reuse defence would log
 * the user out — exactly the F5-multiple-times-and-get-kicked symptom.
 *
 * The promise resolves to `true` on success and clears itself when done so
 * the NEXT refresh (15 min later, on token expiry) is allowed to fire.
 */
import { clearAccessToken, getCsrfToken, setAccessToken } from './tokenStore';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://api.kidzio.io';

let inFlight: Promise<boolean> | null = null;
let lastSuccessAt = 0;

/**
 * Run one /v1/auth/refresh and update tokenStore on success.
 *
 * If a refresh succeeded within the last `cooldownMs` and a caller asks
 * again, we return `true` without hitting the network. This deduplicates
 * the "boot refresh races with first API call" sequence after a hard reload.
 */
export function refreshAccessToken({ cooldownMs = 1500 } = {}): Promise<boolean> {
  if (inFlight) return inFlight;
  if (Date.now() - lastSuccessAt < cooldownMs) return Promise.resolve(true);

  inFlight = (async () => {
    try {
      const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken() ?? '',
        },
      });
      if (!res.ok) {
        clearAccessToken();
        return false;
      }
      const data = (await res.json()) as {
        access_token?: string;
        expires_in?: number;
      };
      if (data.access_token && data.expires_in) {
        setAccessToken(data.access_token, data.expires_in);
        lastSuccessAt = Date.now();
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}
